import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Globe, RefreshCw, ExternalLink, Copy, Check } from "lucide-react";
import { API } from "../lib/api";
import { toast } from "sonner";

export default function WebPreviewDialog({ open, onOpenChange, blueprint }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchPreview = async () => {
    if (!blueprint) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("dpa_token");
      const res = await fetch(`${API}/prompt/web-preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ blueprint }),
      });
      if (!res.ok) throw new Error();
      const { html: payload } = await res.json();
      setHtml(payload || "");
    } catch {
      toast.error("Could not build web preview");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) { setHtml(""); return; }
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, blueprint?.title]);

  const openInNewTab = () => {
    if (!html) return;
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) { toast.error("Pop-up blocked"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const copyHtml = async () => {
    if (!html) return;
    try {
      await navigator.clipboard.writeText(html);
      setCopied(true);
      toast.success("HTML copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-cream border-2 border-ink rounded-3xl max-w-5xl max-h-[92vh] overflow-hidden p-0"
        data-testid="web-preview-dialog"
      >
        <div className="px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
              <Globe className="w-5 h-5 text-marigold" /> Web Demo
            </DialogTitle>
            <DialogDescription className="text-ink2">
              A clickable HTML sandbox of <span className="font-semibold text-ink">"{blueprint?.title}"</span> — share it,
              tweak it, or open it in a new tab. (Static demo, no real backend.)
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={fetchPreview}
              disabled={loading}
              className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition disabled:opacity-40"
              data-testid="web-preview-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Building…" : "Rebuild"}
            </button>
            <button
              onClick={openInNewTab}
              disabled={!html}
              className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition disabled:opacity-40"
              data-testid="web-preview-open-tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
            </button>
            <button
              onClick={copyHtml}
              disabled={!html}
              className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition disabled:opacity-40"
              data-testid="web-preview-copy-html"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-teal" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy HTML"}
            </button>
          </div>
        </div>

        <div className="px-6 pb-6 pt-4">
          <div
            className="rounded-2xl overflow-hidden border-2 border-ink bg-white"
            style={{ height: "70vh" }}
            data-testid="web-preview-frame-wrap"
          >
            {loading && !html ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-paper via-saffron/10 to-marigold/10 animate-pulse">
                <RefreshCw className="w-6 h-6 text-marigold animate-spin" />
                <span className="text-sm text-ink2 font-medium">Sketching your demo…</span>
              </div>
            ) : (
              <iframe
                title="Web demo"
                srcDoc={html}
                sandbox="allow-scripts"
                className="w-full h-full bg-white"
                data-testid="web-preview-iframe"
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
